/**
 * @fileoverview Keeps the signed-in user's capabilities current while the app is open.
 * @description A Super Admin can change role capabilities at any time, so the menu, routes and
 * actions this tab derived from them can go stale. This hook (mounted once, by the dashboard
 * shell) re-fetches the current principal on page navigation, at most once per the query's stale
 * time (30 seconds) so clicking around doesn't cost two requests per click — the tab-focus and `403
 * / 2008` triggers live in useCurrentSession and the response interceptor — and mirrors the result
 * into the persisted auth user, whose privileges and role (kept from login) decide the home route,
 * so a refreshed screen and a refreshed sign-in landing agree. The mirror only ever writes a
 * principal that belongs to the signed-in user, and re-checks whenever the signed-in user changes,
 * so a result left over from the previous user can't overwrite the next one's role and
 * privileges. The first render doesn't re-fetch: the query has just loaded it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router'

import { useCurrentSession } from '@/features/auth/hooks/useCurrentSession'
import { refreshStaleSessionAccess } from '@/shared/lib/auth/access-refresh.utils'
import { useAuthStore } from '@/shared/lib/auth/auth.store'

/**
 * @description Whether two privilege lists hold the same keys, whatever order the server listed
 * them in (a reordering is not a change of access and must not rewrite the stored user).
 * @param {string[]} first - One privilege list.
 * @param {string[]} second - The other privilege list.
 * @returns {boolean} True when they hold the same keys.
 */
function samePrivileges(first: string[], second: string[]): boolean {
  const secondKeys = new Set(second)
  return new Set(first).size === secondKeys.size && first.every((key) => secondKeys.has(key))
}

/**
 * @description Whether a fetched principal is the signed-in user's: the same account email, with
 * case ignored. A principal with no email never matches.
 * @param {string | undefined} principalEmail - The email on the fetched principal.
 * @param {string} userEmail - The email of the signed-in (stored) user.
 * @returns {boolean} True when they are the same account.
 */
function isSameAccount(principalEmail: string | undefined, userEmail: string): boolean {
  return principalEmail?.toLowerCase() === userEmail.toLowerCase()
}

/**
 * @description Re-fetches the user's capabilities on page navigation (throttled by the query's stale
 * time) and writes any change in role/privileges into the persisted auth user.
 */
export function useKeepAccessCurrent(): void {
  const { pathname } = useLocation()
  const { data } = useCurrentSession()
  const user = useAuthStore((state) => state.user)
  const previousPathname = useRef(pathname)

  useEffect(
    /**
     * @description Re-fetches stale capabilities when the page changes, skipping the initial render.
     */
    () => {
      if (previousPathname.current !== pathname) {
        previousPathname.current = pathname
        void refreshStaleSessionAccess()
      }
    },
    [pathname],
  )

  useEffect(
    /**
     * @description Mirrors the latest role and privileges into the stored auth user when they differ
     * — only if the principal is that user's, and again whenever the signed-in user changes.
     */
    () => {
      const privileges = data?.privileges

      if (!user || !privileges || !isSameAccount(data.email, user.email)) {
        return
      }

      const activeRoleKey = data.activeRoleKey ?? ''
      const activeRoleName = data.activeRoleName ?? ''

      if (
        user.activeRoleKey !== activeRoleKey ||
        user.activeRoleName !== activeRoleName ||
        !samePrivileges(user.privileges, privileges)
      ) {
        useAuthStore.getState().setUser({ ...user, activeRoleKey, activeRoleName, privileges })
      }
    },
    [data, user],
  )
}
